import React, {
    useEffect,
    useState,
} from "react";

import {
    getAdminUsers,
    createAdminUser,
    updateAdminUser,
    toggleAdminUser,
    deleteAdminUser,
} from "../../services/adminApi";

import AdminPagination from "./AdminPagination";

import "./AdminUsers.css";
import { showAlert } from "../../util/api";


const emptyForm = {
    name: "",
    email: "",
    password: "",
    password_confirmation: "",
    role: "admin",
    active: true,
};


const AdminUsers = () => {

    const [users, setUsers] =
        useState([]);

    const [pagination, setPagination] =
        useState(null);

    const [page, setPage] =
        useState(1);

    const [search, setSearch] =
        useState("");

    const [role, setRole] =
        useState("");

    const [loading, setLoading] =
        useState(true);

    const [error, setError] =
        useState("");

    const [modalOpen, setModalOpen] =
        useState(false);

    const [editingUser, setEditingUser] =
        useState(null);

    const [form, setForm] =
        useState(emptyForm);

    const [saving, setSaving] =
        useState(false);


    const loadUsers = async () => {

        try {

            setLoading(true);
            setError("");

            const result =
                await getAdminUsers(
                    page,
                    10,
                    {
                        search,
                        role,
                    }
                );

            setUsers(
                result.data?.data || []
            );

            setPagination(
                result.data || null
            );

        } catch (err) {

            setError(
                err.message ||
                "Unable to load administrators."
            );

        } finally {

            setLoading(false);

        }
    };


    useEffect(() => {
        loadUsers();
    }, [page, search, role]);


    const openCreate = () => {

        setEditingUser(null);

        setForm(emptyForm);

        setModalOpen(true);
    };


    const openEdit = (user) => {

        setEditingUser(user);

        setForm({
            name: user.name || "",
            email: user.email || "",
            password: "",
            password_confirmation: "",
            role: user.role || "admin",
            active: user.active,
        });

        setModalOpen(true);
    };


    const closeModal = () => {

        if (saving) return;

        setModalOpen(false);
    };


    const handleChange = (e) => {

        const {
            name,
            value,
            type,
            checked,
        } = e.target;

        setForm((prev) => ({
            ...prev,

            [name]:
                type === "checkbox"
                    ? checked
                    : value,
        }));
    };


    const handleSubmit = async (e) => {

        e.preventDefault();

        try {

            setSaving(true);

            if (editingUser) {

                await updateAdminUser(
                    editingUser.id,
                    form
                );

            } else {

                await createAdminUser(
                    form
                );

            }

            setModalOpen(false);

            await loadUsers();

        } catch (err) {

            
            showAlert('error', 'Error!', err.message || "Unable to save administrator.");

        } finally {

            setSaving(false);
        }
    };


    const handleToggle = async (
        user
    ) => {

        try {

            await toggleAdminUser(
                user.id
            );

            await loadUsers();

        } catch (err) {

            alert(err.message);
        }
    };


    const handleDelete = async (
        user
    ) => {

        const confirmed =
            window.confirm(
                `Delete ${user.name}? This action cannot be undone.`
            );

        if (!confirmed) return;

        try {

            await deleteAdminUser(
                user.id
            );

            await loadUsers();

        } catch (err) {

            alert(err.message);
        }
    };


    return (
        <section className="admin-users-page">

            <div className="admin-page-header">

                <div>

                    <p className="admin-page-label">
                        SYSTEM
                    </p>

                    <h1>
                        Administrators
                    </h1>

                    <p>
                        Manage users who have access
                        to the administration portal.
                    </p>

                </div>

                <button
                    className="admin-primary-button"
                    onClick={openCreate}
                >
                    + Add Administrator
                </button>

            </div>


            <div className="admin-users-toolbar">

                <input
                    type="search"
                    placeholder="Search administrators..."
                    value={search}
                    onChange={(e) => {
                        setSearch(
                            e.target.value
                        );
                        setPage(1);
                    }}
                />

                <select
                    value={role}
                    onChange={(e) => {
                        setRole(
                            e.target.value
                        );
                        setPage(1);
                    }}
                >
                    <option value="">
                        All Roles
                    </option>

                    <option value="super_admin">
                        Super Admin
                    </option>

                    <option value="admin">
                        Admin
                    </option>
                </select>

            </div>


            {loading ? (

                <div className="admin-table-state">
                    Loading administrators...
                </div>

            ) : error ? (

                <div className="admin-table-state error">
                    {error}
                </div>

            ) : (

                <div className="admin-users-table-wrapper">

                    <table className="admin-users-table">

                        <thead>
                            <tr>
                                <th>
                                    Administrator
                                </th>

                                <th>
                                    Role
                                </th>

                                <th>
                                    Status
                                </th>

                                <th>
                                    Last Login
                                </th>

                                <th>
                                    Actions
                                </th>
                            </tr>
                        </thead>

                        <tbody>

                            {users.map(
                                (user) => (

                                    <tr key={user.id}>

                                        <td>

                                            <div className="admin-user-identity">

                                                <div className="admin-user-avatar">
                                                    {user.name
                                                        ?.charAt(0)
                                                        ?.toUpperCase()}
                                                </div>

                                                <div>
                                                    <strong>
                                                        {user.name}
                                                    </strong>

                                                    <span>
                                                        {user.email}
                                                    </span>
                                                </div>

                                            </div>

                                        </td>

                                        <td>
                                            <span
                                                className={`admin-role ${user.role}`}
                                            >
                                                {user.role ===
                                                "super_admin"
                                                    ? "Super Admin"
                                                    : "Admin"}
                                            </span>
                                        </td>

                                        <td>

                                            <button
                                                type="button"
                                                className={`admin-status ${user.active ? "active" : "inactive"}`}
                                                onClick={() =>
                                                    handleToggle(
                                                        user
                                                    )
                                                }
                                            >
                                                <span />

                                                {user.active
                                                    ? "Active"
                                                    : "Inactive"}
                                            </button>

                                        </td>

                                        <td>
                                            {user.last_login_at
                                                ? new Date(
                                                      user.last_login_at
                                                  ).toLocaleString()
                                                : "Never"}
                                        </td>

                                        <td>

                                            <div className="admin-user-actions">

                                                <button  style={{  color : "red" }}
                                                    onClick={() =>
                                                        openEdit(
                                                            user
                                                        )
                                                    }
                                                >
                                                    Edit
                                                </button>

                                                <button  style={{  color : "black" }}
                                                    className="danger"
                                                    onClick={() =>
                                                        handleDelete(
                                                            user
                                                        )
                                                    }
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </td>

                                    </tr>
                                )
                            )}

                        </tbody>

                    </table>

                </div>
            )}


            <AdminPagination
                pagination={pagination}
                onPageChange={setPage}
            />


            {/* MODAL */}

            {modalOpen && (

                <div
                    className="admin-modal-overlay"
                    onMouseDown={closeModal}
                >

                    <div
                        className="admin-user-modal"
                        onMouseDown={(e) =>
                            e.stopPropagation()
                        }
                    >

                        <div className="admin-modal-header">

                            <div>
                                <span>
                                    ADMINISTRATION
                                </span>

                                <h2>
                                    {editingUser
                                        ? "Edit Administrator"
                                        : "Add Administrator"}
                                </h2>
                            </div>

                            <button
                                onClick={closeModal}
                            >
                                ×
                            </button>

                        </div>


                        <form
                            onSubmit={
                                handleSubmit
                            }
                        >

                            <label>
                                Full Name

                                <input
                                    name="name"
                                    value={
                                        form.name
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required
                                />
                            </label>


                            <label>
                                Email Address

                                <input
                                    type="email"
                                    name="email"
                                    value={
                                        form.email
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required
                                />
                            </label>


                            <label>
                                Role

                                <select
                                    name="role"
                                    value={
                                        form.role
                                    }
                                    onChange={
                                        handleChange
                                    }
                                >
                                    <option value="admin">
                                        Admin
                                    </option>

                                    <option value="super_admin">
                                        Super Admin
                                    </option>
                                </select>
                            </label>


                            <label>
                                Password

                                <input
                                    type="password"
                                    name="password"
                                    value={
                                        form.password
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    placeholder={
                                        editingUser
                                            ? "Leave blank to keep current password"
                                            : "Minimum 8 characters"
                                    }
                                    required={
                                        !editingUser
                                    }
                                />
                            </label>


                            <label>
                                Confirm Password

                                <input
                                    type="password"
                                    name="password_confirmation"
                                    value={
                                        form.password_confirmation
                                    }
                                    onChange={
                                        handleChange
                                    }
                                    required={
                                        !editingUser ||
                                        !!form.password
                                    }
                                />
                            </label>


                            <label className="admin-checkbox">

                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={
                                        form.active
                                    }
                                    onChange={
                                        handleChange
                                    }
                                />

                                <span>
                                    Account is active
                                </span>

                            </label>


                            <div className="admin-modal-actions">

                                <button
                                    type="button"
                                    onClick={
                                        closeModal
                                    }
                                >
                                    Cancel
                                </button>

                                <button
                                    type="submit"
                                    className="admin-primary-button"
                                    disabled={
                                        saving
                                    }
                                >
                                    {saving
                                        ? "Saving..."
                                        : "Save Administrator"}
                                </button>

                            </div>

                        </form>

                    </div>

                </div>
            )}

        </section>
    );
};

export default AdminUsers;